// queue — the ordered list of things a crew did while the phone had no
// signal, replayed to POST /api/field/sync when it does.
//
// One op is one tap: clock_in, clock_out, subtask, note, problem. Each gets a
// uuid `key` the moment it is enqueued, and the server treats the key as the
// identity of the op (REBUILD_API.md §1: "a replayed key returns the original
// result"), so sending the same op twice is safe and losing the reply to a
// send is not a loss.
//
// Order matters — a clock_out queued after a clock_in must reach the server
// after it, because it names the clock_in's key as `entry_key`. So replay
// sends in `created` order and stops at the first batch that gets no answer.
//
// Three outcomes per op after a replay, and only these three:
//   ok: true      — done (or already done). Removed.
//   ok: false     — the server answered and said no ("Job not found"). Moved
//                   to the failed list with the sentence, so the banner can
//                   show it and the crew can dismiss it. NOT retried: the
//                   answer will not change.
//   no answer     — NetworkError. Kept exactly as it was, for next time.
//
// Pure of React. Persistence goes through a storage adapter (storage.js); the
// in-memory mirror is what the UI reads synchronously.

import { post, isUnreachable } from '../../shared/http';
import { mayReplay, needsAdoption } from './owner';
import { resilientStore, uuid } from './storage';

export const BATCH_SIZE = 50;
export const SYNC_PATH = '/field/sync';

/**
 * The fields sent to the server — everything but our bookkeeping.
 *
 * `owner` is bookkeeping and MUST stay off the wire. The server attributes an
 * op from the bearer token (server/routes/field.js `withClientKey(db,
 * req.user.id, …)`), and a client-supplied user id reaching a payroll write
 * would be a far worse bug than the one owner exists to fix.
 */
export function wireOp(record) {
  const { created, status, error, owner, ...rest } = record;
  return rest;
}

export function createQueue({ store, now = () => new Date(), newKey = uuid, batchSize = BATCH_SIZE, send, owner = null } = {}) {
  const backing = store || resilientStore('ops', { jsonSafe: true });
  const sendFn = send || ((body) => post(SYNC_PATH, body));
  let items = [];
  let currentOwner = owner;
  const listeners = new Set();
  let lock = Promise.resolve();

  const notify = (event) => { listeners.forEach((fn) => { try { fn(event); } catch (_) { /* a listener must not break the queue */ } }); };
  const sortByCreated = (rows) => rows.slice().sort((a, b) => (a.created < b.created ? -1 : a.created > b.created ? 1 : 0));

  const ready = backing.all()
    .then((rows) => { items = sortByCreated(rows.filter((r) => r && r.key)); notify('loaded'); })
    .catch(() => { items = []; notify('loaded'); });

  const persist = (record) => backing.put(record).catch(() => { /* the mirror still has it for this session */ });
  const erase = (key) => backing.remove(key).catch(() => {});

  const q = {
    ready,
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },

    /** Who this queue is currently replaying for. Set from the signed-in user. */
    setOwner(id) {
      const next = id === undefined || id === null || id === '' ? null : id;
      if (String(next) === String(currentOwner)) return;
      currentOwner = next;
      notify('changed');
    },
    owner() { return currentOwner; },

    /**
     * Adopt records that predate owner stamping. One-time, and only for
     * records with NO owner — see owner.js for why this is adoption rather
     * than stranding or dropping.
     */
    adoptOrphans() {
      if (currentOwner === null) return 0;
      const orphans = items.filter(needsAdoption);
      if (!orphans.length) return 0;
      const adopted = orphans.map((r) => ({ ...r, owner: currentOwner }));
      const byKey = new Map(adopted.map((r) => [r.key, r]));
      items = items.map((r) => byKey.get(r.key) || r);
      adopted.forEach(persist);
      notify('changed');
      return adopted.length;
    },

    enqueue(op) {
      const ts = now().toISOString();
      const record = { ...op, key: op.key || newKey(), at: op.at || ts, created: ts, status: 'pending', owner: currentOwner };
      delete record.error;
      items = sortByCreated([...items.filter((r) => r.key !== record.key), record]);
      persist(record);
      notify('changed');
      return record;
    },

    pending() { return items.filter((r) => r.status !== 'failed'); },
    failed() { return items.filter((r) => r.status === 'failed'); },
    has(key) { return items.some((r) => r.key === key && r.status !== 'failed'); },
    get(key) { return items.find((r) => r.key === key) || null; },

    remove(key) {
      const before = items.length;
      items = items.filter((r) => r.key !== key);
      erase(key);
      if (items.length !== before) notify('changed');
    },

    /**
     * Take a refusal off the banner. Only a FAILED record is dismissible.
     *
     * The status check is the whole point: dismiss means "stop showing me this
     * refusal", never "delete my unsent clock-in". Filtering on the key alone
     * dropped whatever record carried it — and a PENDING one reaches here
     * (useOffline routes on `q.get(key)`, which finds a record whatever its
     * status), so a tap on the banner could erase work the crew member watched
     * the app accept, from the mirror AND from storage, silently. That is
     * unpaid hours.
     */
    dismissFailed(key) {
      const before = items.length;
      if (key === undefined) {
        for (const r of items.filter((x) => x.status === 'failed')) erase(r.key);
        items = items.filter((r) => r.status !== 'failed');
      } else {
        const target = items.find((r) => r.key === key && r.status === 'failed');
        if (!target) return;
        items = items.filter((r) => r !== target);
        erase(key);
      }
      if (items.length !== before) notify('changed');
    },

    /**
     * Send everything pending, in order, in batches. Serialised: a second
     * call while one is running waits for it rather than racing it.
     * Resolves { sent, failed, kept, offline, error } — never rejects.
     */
    replay(sendOverride) {
      const doSend = sendOverride || sendFn;
      const run = async () => {
        await ready;
        const out = { sent: 0, failed: 0, kept: 0, offline: false, error: null };
        let cursor = 0;
        while (true) {
          // Only this person's work. Somebody else's ops are neither sent nor
          // deleted — they wait for their owner to sign in. Without this the
          // POST below carries the WRONG bearer token and writes a colleague's
          // clock-in as this person's time entry (owner.js).
          // Read once per pass into a const: the closure below must not
          // capture the mutable binding (no-loop-func), and a sign-out
          // landing mid-replay should take effect on the NEXT pass rather
          // than half way through one.
          const me = currentOwner;
          const mine = q.pending().filter((r) => mayReplay(r, me));
          const batch = mine.slice(cursor, cursor + batchSize);
          if (batch.length === 0) break;
          let res;
          try {
            res = await doSend({ ops: batch.map(wireOp) });
          } catch (e) {
            if (isUnreachable(e)) out.offline = true; else out.error = e;
            out.kept += mine.length - cursor;
            break;
          }
          const results = new Map(((res && res.results) || []).filter((r) => r && r.key).map((r) => [r.key, r]));
          let advanced = 0;
          for (const record of batch) {
            const r = results.get(record.key);
            if (!r) { out.kept++; advanced++; continue; }
            if (r.ok) {
              items = items.filter((x) => x.key !== record.key);
              erase(record.key);
              out.sent++;
            } else {
              const failedRec = { ...record, status: 'failed', error: r.error || 'Could not be sent' };
              items = items.map((x) => (x.key === record.key ? failedRec : x));
              persist(failedRec);
              out.failed++;
            }
          }
          cursor += advanced;
          notify('changed');
        }
        if (out.sent > 0 || out.failed > 0) notify('replayed');
        return out;
      };
      const p = lock.then(run, run);
      lock = p.catch(() => {});
      return p;
    },
  };
  return q;
}

let shared = null;
/** The one queue the app uses. Created on first use so importing is free. */
export function getQueue() {
  if (!shared) shared = createQueue();
  return shared;
}
/** Test seam: replace the shared queue. */
export function setQueue(q) { shared = q; }
